import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';

interface SubscriptionRow {
  readonly subscriptionId: string;
  readonly consumerName: string;
  readonly status: string;
  readonly rateLimit: number;
}

export function M20ApiSubscriptionPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<SubscriptionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [apiId, setApiId] = useState('');
  const [consumerId, setConsumerId] = useState('');
  const [consumerName, setConsumerName] = useState('');
  const [rateLimit, setRateLimit] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: SubscriptionRow[] }>('/v1/integration/subscriptions', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/integration/subscriptions', {
        method: 'POST', token,
        body: {
          apiId,
          consumerId,
          consumerName,
          rateLimit: rateLimit ? Number(rateLimit) : undefined,
        },
      });
      setCreated(true);
      setApiId('');
      setConsumerId('');
      setConsumerName('');
      setRateLimit('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const act = async (id: string, action: 'suspend' | 'revoke'): Promise<void> => {
    setBusyId(id + action);
    try {
      await apiRequest(`/v1/integration/subscriptions/${id}/${action}`, { method: 'POST', token });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m20.apiSubscription.loading')}</p>;
  return (
    <main aria-label="api-subscriptions">
      <h1>{t('m20.apiSubscription.title')}</h1>

      <form onSubmit={onCreate} aria-label="subscription-create-form">
        <h2>{t('m20.apiSubscription.newSubscription')}</h2>
        <RecordPicker<{ apiId: string; apiCode: string; apiName: string; version: string }>
          endpoint="/v1/integration/gateway/apis?limit=200"
          idField="apiId"
          label={t('m20.apiSubscription.apiId')}
          testId="sub-api"
          value={apiId}
          optionLabel={(a) => `${a.apiCode} \u2014 ${a.apiName} (${a.version})`}
          onPick={(id) => setApiId(id)}
        />
        <label htmlFor="sub-consumer-id">{t('m20.apiSubscription.consumerId')}</label>
        <input id="sub-consumer-id" value={consumerId} onChange={(e) => setConsumerId(e.target.value)} required />
        <label htmlFor="sub-consumer-name">{t('m20.apiSubscription.consumerName')}</label>
        <input id="sub-consumer-name" value={consumerName} onChange={(e) => setConsumerName(e.target.value)} required />
        <label htmlFor="sub-rate">{t('m20.apiSubscription.rateLimit')}</label>
        <input id="sub-rate" type="number" value={rateLimit} onChange={(e) => setRateLimit(e.target.value)} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m20.apiSubscription.createdMsg')}</p> : null}
        <button type="submit" disabled={creating || !apiId}>{creating ? t('m20.apiSubscription.creating') : t('m20.apiSubscription.createBtn')}</button>
      </form>

      <table aria-label="api-subscriptions-table">
        <thead><tr><th>{t('m20.apiSubscription.colConsumer')}</th><th>{t('m20.apiSubscription.colStatus')}</th><th>{t('m20.apiSubscription.colRateLimit')}</th><th>{t('m20.apiSubscription.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.subscriptionId}>
              <td>{s.consumerName}</td>
              <td>{s.status}</td>
              <td>{s.rateLimit}</td>
              <td>
                <button type="button" aria-label={`suspend-${s.subscriptionId}`} disabled={busyId === s.subscriptionId + 'suspend'} onClick={() => void act(s.subscriptionId, 'suspend')}>{t('m20.apiSubscription.suspend')}</button>
                <button type="button" aria-label={`revoke-${s.subscriptionId}`} disabled={busyId === s.subscriptionId + 'revoke'} onClick={() => void act(s.subscriptionId, 'revoke')}>{t('m20.apiSubscription.revoke')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
